import { matchPath } from 'react-router-dom'

/**
 * Page context sent to ELARA with every text-chat request (inside `businessMeta`).
 * The backend reads these to scope tool groups and seed the prompt ("Where the user is").
 */
export interface ElaraPageContext {
    /** Current router pathname */
    route: string
    patientId?: string
    consultationId?: string
    expertiseId?: string
    waitingRoomId?: string
}

/** Literal segments that share a position with an entity id in the app routes. */
const NON_ID_SEGMENTS = new Set(['create', 'local', 'merge', 'live', 'update', 'validation', 'edit', 'reserve'])

const isEntityId = (value: string | undefined): value is string => !!value && !NON_ID_SEGMENTS.has(value)

/** Route patterns of the professional app that carry an entity id (see App.tsx). */
const ROUTE_PATTERNS: Array<{ pattern: string; assign: (params: Record<string, string | undefined>, out: ElaraPageContext) => void }> = [
    { pattern: '/patients/:patientId/*', assign: (p, out) => { if (isEntityId(p.patientId)) out.patientId = p.patientId } },
    { pattern: '/consultations/:consultationId/*', assign: (p, out) => { if (isEntityId(p.consultationId)) out.consultationId = p.consultationId } },
    { pattern: '/offline/consultations/:consultationId/*', assign: (p, out) => { if (isEntityId(p.consultationId)) out.consultationId = p.consultationId } },
    { pattern: '/connector/netsoins/:consultationId', assign: (p, out) => { if (isEntityId(p.consultationId)) out.consultationId = p.consultationId } },
    { pattern: '/expertises/:expertiseId/*', assign: (p, out) => { if (isEntityId(p.expertiseId)) out.expertiseId = p.expertiseId } },
    { pattern: '/waiting-rooms/:waitingRoomId/*', assign: (p, out) => { if (isEntityId(p.waitingRoomId)) out.waitingRoomId = p.waitingRoomId } },
    { pattern: '/forms/submit/:templateId/:patientId/*', assign: (p, out) => { if (isEntityId(p.patientId)) out.patientId = p.patientId } },
]

/**
 * Derives the page context from a pathname. Pure — exported for tests and for callers
 * that are outside the router.
 */
export function deriveElaraPageContext(pathname: string): ElaraPageContext {
    const out: ElaraPageContext = { route: pathname || '/' }
    for (const { pattern, assign } of ROUTE_PATTERNS) {
        const match = matchPath({ path: pattern, end: false }, pathname)
        if (match) assign(match.params as Record<string, string | undefined>, out)
    }
    return out
}

/** Removes undefined keys so the envelope stays small. */
export function compactPageContext(ctx: ElaraPageContext): ElaraPageContext {
    return Object.fromEntries(Object.entries(ctx).filter(([, v]) => v !== undefined)) as ElaraPageContext
}
